import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import type {
  CertEmployeeNotice,
  CertNotifyItem,
  CertNotifyOverview,
  CertNotifySendResult,
  CertNotifySettings,
} from '@/api/types'
import { dayLabel } from '@/lib/certs'

/**
 * Certification expiry alerts by email (migration 30, api/app/cert_notify.py).
 *
 * Built ahead of the decisions: who receives the office summary and when it
 * runs are NW Linings' call (Tom, 13 Oct 2026). Both switches start off and
 * the office list starts empty, so this screen can be opened, previewed and
 * tested without anything going to anyone.
 *
 * "Due now" is the same plan Send now and the scheduled script use, so what
 * is previewed is what is sent. Office reads it; Admin changes and sends.
 */

const NOTICE_LABEL: Record<CertEmployeeNotice, string> = {
  emailed: 'Will be emailed',
  already_sent: 'Emailed earlier',
  no_address: 'No email on file',
  opted_out: 'Opted out',
  off: 'Employee alerts off',
}

const NOTICE_BADGE: Record<CertEmployeeNotice, string> = {
  emailed: 'badge-ok',
  already_sent: 'badge-info',
  no_address: 'badge-warn',
  opted_out: 'badge-info',
  off: 'badge-info',
}

function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message
  return err instanceof Error ? err.message : fallback
}

function statusText(i: CertNotifyItem): string {
  if (i.expires_date === null) return i.stage === -1 ? 'Expired' : 'Due for renewal (medical: no date shown)'
  const d = i.days_to_expiry ?? 0
  if (d < 0) return `Expired ${dayLabel(i.expires_date)}`
  if (d === 0) return `Expires today`
  return `Expires ${dayLabel(i.expires_date)} (${d} day${d === 1 ? '' : 's'})`
}

function stageText(stage: number | null): string {
  if (stage === null) return '—'
  return stage === -1 ? 'Expired notice' : `${stage}-day`
}

export function CertNotificationsPage() {
  const [data, setData] = useState<CertNotifyOverview | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [results, setResults] = useState<CertNotifySendResult[] | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setError(null)
    try {
      setData(await api.certNotify())
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 404
          ? 'Certifications are not switched on for your account.'
          : errText(err, 'Could not load notifications'),
      )
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  async function run(label: string, fn: () => Promise<CertNotifyOverview>): Promise<boolean> {
    setError(null)
    setNotice(null)
    setBusy(true)
    try {
      setData(await fn())
      setNotice(label)
      return true
    } catch (err) {
      setError(errText(err, 'Could not save. Nothing was changed.'))
      return false
    } finally {
      setBusy(false)
    }
  }

  async function send(kind: 'alerts' | 'test', to?: string) {
    setError(null)
    setNotice(null)
    setResults(null)
    setBusy(true)
    try {
      const r = kind === 'alerts' ? await api.sendCertAlerts() : await api.sendCertTestEmail(to ?? '')
      setData(r.overview)
      setResults(r.results)
      const sent = r.results.filter((x) => x.status === 'Sent').length
      setNotice(
        r.results.length === 0
          ? 'Nothing new to send.'
          : `${sent} of ${r.results.length} email${r.results.length === 1 ? '' : 's'} sent.`,
      )
    } catch (err) {
      setError(errText(err, 'Could not send.'))
    } finally {
      setBusy(false)
    }
  }

  if (!data) {
    return (
      <div className="stack">
        {error ? <div className="alert alert-error">{error}</div> : <p className="muted">Loading…</p>}
      </div>
    )
  }

  const { settings, preview } = data
  const manage = data.can_manage
  const sendable = preview.messages.filter((m) => m.will_send)

  return (
    <div className="stack">
      {error && <div className="alert alert-error">{error}</div>}
      {notice && <div className="alert alert-ok">{notice}</div>}

      <div className="card">
        <h2 style={{ marginTop: 0 }}>Expiry alerts</h2>
        <p className="muted">
          Emails the employee, and an office summary, when a certification enters its warning window,
          at each reminder below, and once when it expires. Each certification is emailed once per
          stage; renewing it starts again. Medical certifications are named without dates.
        </p>
        {!data.mail_configured && (
          <div className="alert alert-warn">
            Email is not set up on this server yet (no Resend key). Everything below works except
            sending.
          </div>
        )}
        {data.allow_only !== null && (
          <div className="alert alert-warn">
            Test server: email goes only to{' '}
            {data.allow_only.length ? data.allow_only.join(', ') : 'nobody'}. Anything else is logged
            as Blocked.
          </div>
        )}
        <p className="muted" style={{ margin: 0 }}>
          From <b>{data.sender}</b>
          {data.reply_to ? <> · replies to <b>{data.reply_to}</b></> : ' · no reply-to address set'}
        </p>
      </div>

      <SettingsCard
        settings={settings}
        canEdit={manage}
        busy={busy}
        onSave={(s) => run('Settings saved.', () => api.updateCertNotifySettings(s))}
      />

      <RecipientsCard
        data={data}
        canEdit={manage}
        busy={busy}
        onAdd={(email, name) =>
          run('Recipient added.', () => api.addCertNotifyRecipient({ email, display_name: name }))
        }
        onToggle={(id, active) =>
          run(active ? 'Recipient switched back on.' : 'Recipient switched off.', () =>
            api.updateCertNotifyRecipient(id, { is_active: active }),
          )
        }
      />

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Due now</h3>
        <p className="muted">As of {dayLabel(preview.as_of)}. Only what has not been emailed at this stage yet.</p>
        {preview.office_items.length === 0 && preview.employee_items.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nothing is due.</p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>Person</th>
                  <th>Certification</th>
                  <th>Status</th>
                  <th>Stage</th>
                  <th>Employee</th>
                </tr>
              </thead>
              <tbody>
                {mergeItems(preview.office_items, preview.employee_items).map((i) => (
                  <tr key={i.employee_cert_id}>
                    <td>
                      <Link to={`/certs/people/${i.employee_id}`}>{i.person}</Link>{' '}
                      <span className="muted">{i.employee_no}</span>
                    </td>
                    <td>{i.type_name}</td>
                    <td>{statusText(i)}</td>
                    <td className="muted">{stageText(i.stage)}</td>
                    <td>
                      <span className={`badge ${NOTICE_BADGE[i.employee_notice]}`}>
                        {NOTICE_LABEL[i.employee_notice]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {preview.unreachable.length > 0 && (
          <p className="muted">
            Can't be emailed:{' '}
            {preview.unreachable.map((u, n) => (
              <span key={u.employee_id}>
                {n > 0 && ', '}
                <Link to={`/certs/people/${u.employee_id}`}>{u.person}</Link>
                {u.reason === 'opted_out' ? ' (opted out)' : ''}
              </span>
            ))}
            . Add an alert email on their certifications page.
          </p>
        )}
        {preview.office_without_recipients && (
          <p className="muted">The office summary has nobody to go to: add a recipient above.</p>
        )}

        <h4>Emails that would go</h4>
        {preview.messages.length === 0 ? (
          <p className="muted">None.</p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>To</th>
                  <th>Kind</th>
                  <th>Subject</th>
                  <th>Certs</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {preview.messages.map((m, n) => (
                  <tr key={n} style={m.will_send ? undefined : { opacity: 0.6 }}>
                    <td>
                      {m.to_name} <span className="muted">{m.to_email}</span>
                    </td>
                    <td>{m.kind}</td>
                    <td>{m.subject}</td>
                    <td>{m.item_count}</td>
                    <td className="muted">{m.will_send ? '' : 'switched off'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {manage && (
          <div className="row" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="btn"
              disabled={busy || !data.mail_configured || sendable.length === 0}
              onClick={() => {
                if (window.confirm(`Send ${sendable.length} email${sendable.length === 1 ? '' : 's'} now?`)) {
                  void send('alerts')
                }
              }}
            >
              Send now ({sendable.length})
            </button>
            {sendable.length === 0 && preview.messages.length > 0 && (
              <span className="muted">Turn on employee or office alerts to send these.</span>
            )}
          </div>
        )}
        {results && results.length > 0 && <ResultsTable results={results} />}
      </div>

      {manage && <TestCard busy={busy} configured={data.mail_configured} onSend={(to) => void send('test', to)} />}

      <div className="card">
        <h3 style={{ marginTop: 0 }}>Sent log</h3>
        {data.log.length === 0 ? (
          <p className="muted" style={{ margin: 0 }}>Nothing has been sent.</p>
        ) : (
          <div className="table-wrap table-dense">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>To</th>
                  <th>Kind</th>
                  <th>Subject</th>
                  <th>Status</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {data.log.map((r) => (
                  <tr key={r.cert_notify_message_id}>
                    <td className="muted">{new Date(r.created_utc + 'Z').toLocaleString()}</td>
                    <td>{r.to_email}</td>
                    <td>{r.kind}</td>
                    <td>
                      {r.subject}
                      {r.item_count > 0 && <span className="muted"> · {r.item_count} cert{r.item_count === 1 ? '' : 's'}</span>}
                    </td>
                    <td>
                      <span className={`badge ${r.status === 'Sent' ? 'badge-ok' : r.status === 'Failed' ? 'badge-error' : 'badge-warn'}`}>
                        {r.status}
                      </span>
                      {r.error && <div className="muted">{r.error}</div>}
                    </td>
                    <td className="muted">{r.trigger_type === 'Schedule' ? 'Scheduled' : r.actor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

/** One row per cert: the office list, plus any the office has already had
 *  but the employee has not. */
function mergeItems(office: CertNotifyItem[], employee: CertNotifyItem[]): CertNotifyItem[] {
  const seen = new Set(office.map((i) => i.employee_cert_id))
  return [...office, ...employee.filter((i) => !seen.has(i.employee_cert_id))]
}

function ResultsTable({ results }: { results: CertNotifySendResult[] }) {
  return (
    <div className="table-wrap table-dense" style={{ marginTop: 12 }}>
      <table>
        <thead>
          <tr>
            <th>To</th>
            <th>Kind</th>
            <th>Result</th>
          </tr>
        </thead>
        <tbody>
          {results.map((r, n) => (
            <tr key={n}>
              <td>{r.to_email}</td>
              <td>{r.kind}</td>
              <td>
                <span className={`badge ${r.status === 'Sent' ? 'badge-ok' : r.status === 'Failed' ? 'badge-error' : 'badge-warn'}`}>
                  {r.status}
                </span>
                {r.error && <span className="muted"> {r.error}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function SettingsCard({
  settings,
  canEdit,
  busy,
  onSave,
}: {
  settings: CertNotifySettings
  canEdit: boolean
  busy: boolean
  onSave: (s: CertNotifySettings) => Promise<boolean>
}) {
  const [form, setForm] = useState(settings)
  useEffect(() => setForm(settings), [settings])
  const dirty =
    form.employee_alerts_enabled !== settings.employee_alerts_enabled ||
    form.office_alerts_enabled !== settings.office_alerts_enabled ||
    form.reminder_days !== settings.reminder_days ||
    form.send_expired_notice !== settings.send_expired_notice

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Settings</h3>
      <label className="checkline">
        <input
          type="checkbox"
          disabled={!canEdit}
          checked={form.employee_alerts_enabled}
          onChange={(e) => setForm({ ...form, employee_alerts_enabled: e.target.checked })}
        />{' '}
        Email employees about their own certifications
      </label>
      <label className="checkline">
        <input
          type="checkbox"
          disabled={!canEdit}
          checked={form.office_alerts_enabled}
          onChange={(e) => setForm({ ...form, office_alerts_enabled: e.target.checked })}
        />{' '}
        Email the office summary to the recipients below
      </label>
      <label className="checkline">
        <input
          type="checkbox"
          disabled={!canEdit}
          checked={form.send_expired_notice}
          onChange={(e) => setForm({ ...form, send_expired_notice: e.target.checked })}
        />{' '}
        Send a notice when a certification expires (within 30 days of it lapsing)
      </label>
      <div className="form-field" style={{ maxWidth: 320 }}>
        <label htmlFor="ntReminders">Reminders, days before expiry</label>
        <input
          id="ntReminders"
          disabled={!canEdit}
          value={form.reminder_days}
          placeholder="e.g. 14,7"
          onChange={(e) => setForm({ ...form, reminder_days: e.target.value })}
        />
        <span className="muted">
          The first alert is on the day each type's warning window starts (30 days unless set on the
          type). These are extra reminders after it.
        </span>
      </div>
      {settings.updated_by && (
        <p className="muted">
          Last changed by {settings.updated_by}
          {settings.updated_utc && `, ${new Date(settings.updated_utc + 'Z').toLocaleString()}`}
        </p>
      )}
      {canEdit ? (
        <button className="btn" disabled={busy || !dirty} onClick={() => void onSave(form)}>
          Save settings
        </button>
      ) : (
        <p className="muted" style={{ margin: 0 }}>Only an Admin can change these.</p>
      )}
    </div>
  )
}

function RecipientsCard({
  data,
  canEdit,
  busy,
  onAdd,
  onToggle,
}: {
  data: CertNotifyOverview
  canEdit: boolean
  busy: boolean
  onAdd: (email: string, name: string) => Promise<boolean>
  onToggle: (id: number, active: boolean) => Promise<boolean>
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Office summary recipients</h3>
      {data.recipients.length === 0 ? (
        <p className="muted">Nobody yet.</p>
      ) : (
        <div className="table-wrap table-dense">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.recipients.map((r) => (
                <tr key={r.cert_notify_recipient_id} style={r.is_active ? undefined : { opacity: 0.6 }}>
                  <td>{r.display_name}</td>
                  <td>{r.email}</td>
                  <td>
                    {canEdit && (
                      <button
                        className="btn btn-secondary btn-sm"
                        disabled={busy}
                        onClick={() => void onToggle(r.cert_notify_recipient_id, !r.is_active)}
                      >
                        {r.is_active ? 'Switch off' : 'Switch on'}
                      </button>
                    )}
                    {!r.is_active && !canEdit && <span className="muted">off</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canEdit && (
        <div className="field-grid">
          <div className="form-field">
            <label htmlFor="rcName">Name</label>
            <input id="rcName" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-field">
            <label htmlFor="rcEmail">Email</label>
            <input id="rcEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="form-field" style={{ alignSelf: 'end' }}>
            <button
              className="btn"
              disabled={busy || !email.trim() || !name.trim()}
              onClick={async () => {
                if (await onAdd(email.trim(), name.trim())) {
                  setEmail('')
                  setName('')
                }
              }}
            >
              Add recipient
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function TestCard({
  busy,
  configured,
  onSend,
}: {
  busy: boolean
  configured: boolean
  onSend: (to: string) => void
}) {
  const [to, setTo] = useState('')
  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>Send a test email</h3>
      <p className="muted">
        Proves email from the portal arrives, before switching alerts on. Works with both switches off.
      </p>
      <div className="field-grid">
        <div className="form-field">
          <label htmlFor="tsTo">To</label>
          <input id="tsTo" type="email" value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <div className="form-field" style={{ alignSelf: 'end' }}>
          <button className="btn btn-secondary" disabled={busy || !configured || !to.trim()} onClick={() => onSend(to.trim())}>
            Send test
          </button>
        </div>
      </div>
    </div>
  )
}
